import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectedItemChange } from './selected-item-change';
import { GoogleSearchLocation } from './google-search-location';
import { Country } from '@core/models/common/country';
import { Address } from '@core/models/common/address';
import { SessionService } from '@core/state/session/session.service';
import { WindowRef } from '@vendor/window/window.component';
import { isString } from '@core/utils/string';
import { $log } from '@core/utils/logger';
import { GoogleNormalisedAddress } from '@vendor/google/google-normalised-address';
import { PlacesService } from '@vendor/google/places.service';
import { AutocompleteService } from '@vendor/google/autocomplete.service';

/* eslint-disable @angular-eslint/no-forward-ref */
@Component({
    selector: 'ihd-google-autocomplete',
    templateUrl: 'google-autocomplete.component.html',
    styleUrls: ['./google-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GoogleAutocompleteComponent),
            multi: true,
        },
    ],
    host: {
        class: 'c-google-autocomplete',
        '[class.is-open]': 'isShowingSearch',
        '(focus)': 'setInputFocus()',
    },
})
export class GoogleAutocompleteComponent
    implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {
    @HostBinding('tabindex')
    @Input()
    tabindex?: number = 0;

    /**
     * Used to override the search options provided when looking for results
     * For eg, the results default to use the lat/lng provided by /geo call which is user's current location
     * If you want to override the lat/lng with specific location address you can pass it via this input
     */
    @Input()
    searchLocation?: GoogleSearchLocation;

    /**
     * Use this override the default search radius.
     */
    @Input()
    searchAddress?: number = 13000;

    @Input() searchTerm?: string = '';
    @Input() allowCustom?: boolean = false;
    @Input() placeholder?: string = '';
    @Output() change: EventEmitter<any> = new EventEmitter();
    @Output() searchChange: EventEmitter<any> = new EventEmitter();
    @Output() searchNoResults: EventEmitter<any> = new EventEmitter();
    @Output()
    selectedChange: EventEmitter<SelectedItemChange> = new EventEmitter<SelectedItemChange>();

    @ViewChild('searchInput', { static: true })
    public searchInputRef: ElementRef;

    public isShowingSearch: boolean = false;
    public defaultMatch: any = {};
    public selectedMatches: any[] = [];
    public hasGoogleSuggestions: boolean = false;
    public isLoading: boolean;
    public isFetching: boolean = false;
    public popoverWidth: number = 0;

    public onChange: any;
    public onTouched: any;

    private destroyed$: Subject<void> = new Subject();
    private currentCountry: Country;
    private address: Address;
    private isSelectedBlur: boolean = false;

    constructor(
        private autocompleteService: AutocompleteService,
        private placesService: PlacesService,
        private sessionService: SessionService,
        private windowRef: WindowRef,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {}

    ngOnInit() {
        this.sessionService
            .getCountry()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((country: Country) => {
                this.currentCountry = country;
            });
    }

    ngOnDestroy() {
        this.autocompleteService.clearStorage();
        this.destroyed$.next();
    }

    ngAfterViewInit() {
        this.popoverWidth = this.searchInputRef.nativeElement.offsetWidth;

        fromEvent<KeyboardEvent>(this.searchInputRef.nativeElement, 'keyup')
            .pipe(delay(200), takeUntil(this.destroyed$))
            .subscribe((event: KeyboardEvent) => this.inputSearchChange(event));

        this.cdr.markForCheck();
    }

    writeValue(value: Address | string) {
        let dispatchChange: boolean = false;
        if (value) {
            if (isString(value)) {
                // Don't auto search as could just be label
                this.searchTerm = value as string;
            } else {
                // Valid address
                if (value !== this.address) {
                    this.address = value as Address;
                    this.searchTerm =
                        (this.address && this.address.address) || '';
                    dispatchChange = true;
                }
            }
        } else {
            if (value !== this.address) {
                this.address = null;
                this.searchTerm = '';
                dispatchChange = true;
            }
        }

        if (dispatchChange) {
            if (this.onChange) {
                this.onChange(value);
            }

            // Component change event
            this.change.emit(this.address);
        }

        this.cdr.markForCheck();
    }

    /**
     * Set the function to be called
     * when the control receives a change event.
     */
    registerOnChange(fn: (_: any) => {}) {
        this.onChange = fn;
    }

    /**
     * Set the function to be called
     * when the control receives a touch event.
     */
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    setInputFocus() {
        this.searchInputRef.nativeElement.focus();
    }

    /**
     * Internal search input change event handler.
     * @param event
     */
    inputSearchChange(event: KeyboardEvent) {
        this.isSelectedBlur = false;

        if (event.code === 'Escape') {
            this.searchInputRef.nativeElement.blur();
            this.inputBlur(event);
        } else if (event.code === 'Enter') {
            this.autoSelectMatch(event);
        } else {
            const searchValue: string = (event.target as HTMLInputElement)
                .value;

            if (searchValue !== this.searchTerm) {
                this.searchTerm = searchValue;

                this.isLoading = true;
                this.selectedMatches.length = 0;
                this.isShowingSearch = searchValue && searchValue.length > 0;
                this.cdr.markForCheck();

                this.search(searchValue);
            }
        }
    }

    /**
     * Input blue event debounced so click selection events occur before.
     * @param {Event} event
     */
    inputBlur(event: Event) {
        if (this.isSelectedBlur) {
            this.isSelectedBlur = false;
        } else if (!this.isFetching) {
            // Revert the search value
            if (this.address && this.address.address) {
                this.searchTerm = this.address.address;
            } else if (this.allowCustom && this.searchTerm?.length) {
                this.selectMatch(undefined, this.defaultMatch);
            } else {
                this.address = null;
                this.searchTerm = '';
            }
            this.isLoading = false;
            this.isShowingSearch = false;
        }

        this.cdr.detectChanges();
    }

    search(searchValue: string) {
        const w: any = this.windowRef.nativeWindow;

        // Custom address with no google match
        if (this.allowCustom) {
            this.defaultMatch.label = this.searchTerm;
            this.selectedMatches.push(this.defaultMatch);
        }

        // Narrow results based on location
        let searchOptions: any = {};
        if (
            this.searchLocation &&
            this.searchLocation.lat &&
            this.searchLocation.lng
        ) {
            // use search location if provided as input
            searchOptions = {
                location: new w.google.maps.LatLng({
                    lat: this.searchLocation.lat,
                    lng: this.searchLocation.lng,
                }),
                radius: this.searchAddress,
            };
        } else if (this.currentCountry) {
            // else try to default to one we get from /geo
            searchOptions = {
                location: new w.google.maps.LatLng({
                    lat: this.currentCountry.lat,
                    lng: this.currentCountry.lng,
                }),
                radius: this.searchAddress,
            };
        }

        if (this.searchTerm.length === 0 && this.onChange) {
            // If the user clears out the selection, we need to notify the form control as 'no selection'
            this.writeValue(null);
            this.cdr.detectChanges();
        } else {
            this.autocompleteService
                .getPlacePredictions(this.searchTerm, searchOptions)
                .then(
                    (results) => {

                        if (results && results.length > 0) {
                            const resp = [];

                            results.forEach((result) => {
                                resp.push({
                                    googlePlaceId: result.place_id,
                                    googleResult: result,
                                    label: result.description,
                                    search: this.searchTerm,
                                });
                            });
                            this.hasGoogleSuggestions =
                                results && results.length > 0;
                            this.selectedMatches = [
                                ...this.selectedMatches,
                                ...resp,
                            ];
                        } else {
                            // emit the search term that caused no results to trigger
                            this.searchNoResults.emit({
                                searchTerm: this.searchTerm,
                            });
                        }
                        this.isLoading = false;
                        this.cdr.detectChanges();
                    },
                    (error: any) => {
                        // Error: google place lookup failed
                        $log.error(error);
                        this.hasGoogleSuggestions = false;
                        this.isLoading = false;
                        this.cdr.detectChanges();
                    }
                );
        }

        this.searchChange.emit({
            search: this.searchTerm,
        });
    }

    public trackIdFn(index, data) {
        return data ? data.googlePlaceId : index;
    }

    public autoSelectMatch(event: Event) {
        if (this.selectedMatches.length > 0) {
            const autoSelection = this.selectedMatches[0];
            this.selectMatch(event, autoSelection);
        }
    }

    public selectMatch(event: Event, match: any) {
        event?.stopPropagation();

        this.isSelectedBlur = true;
        this.isShowingSearch = false;
        this.searchTerm = match.label;
        this.isFetching = true;
        this.cdr.detectChanges();

        if (match.googlePlaceId) {
            this.placesService.getDetails(match.googlePlaceId).then(
                (res) => {
                    const normalisedAddress: GoogleNormalisedAddress = this.placesService.normaliseAddressFromPlace(
                        res
                    );
                    const coordinates: any = this.placesService.getCoordinates(
                        res
                    );

                    const addressUpdate: Address = Object.assign(
                        new Address(),
                        {
                            goo_place_id: match.googlePlaceId,
                            lat: coordinates.lat,
                            lng: coordinates.lng,
                            address: match.label, // mandatory
                            street: this.placesService.getStreetLine1(
                                normalisedAddress
                            ),
                            street_two: normalisedAddress.locality,
                            city:
                                normalisedAddress.administrative_area_level_2 ||
                                normalisedAddress.locality,
                            zipcode: normalisedAddress.postal_code,
                            state:
                                normalisedAddress.administrative_area_level_1_short,
                            country: normalisedAddress.country_short,
                            utc_offset: res.utc_offset_minutes,
                            googleName: res.name,
                        }
                    );

                    this.writeValue(addressUpdate);
                    this.isFetching = false;
                    this.cdr.markForCheck();
                },
                (error: any) => {
                    // Error: google details lookup failed
                    $log.error(error);
                    this.isLoading = false;
                    this.isFetching = false;
                    this.cdr.markForCheck();
                }
            );
        } else {
            const addressUpdate: Address = Object.assign(new Address(), {
                address: match.label,
            });

            this.writeValue(addressUpdate);
            this.isFetching = false;
            this.cdr.markForCheck();
        }

        this.selectedChange.emit(match);
    }
}
